import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import {
  Mail, CheckCircle, Loader2, Unplug, Send, Plus, AlertTriangle,
} from 'lucide-react';
import { Button } from './ui/button';

/**
 * Multi-account Gmail connection panel.
 *
 * Lists every known sender (invoices @ accounts@…, career/HR @ hr@…) and
 * lets the admin Connect / Send-test / Disconnect each one independently.
 */
export default function GmailConnect() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectingEmail, setConnectingEmail] = useState(null);
  const [testingEmail, setTestingEmail] = useState(null);

  const fetchAccounts = async () => {
    try {
      const res = await adminApi.get('/api/admin/email/gmail/accounts');
      setAccounts(res.data?.accounts || []);
    } catch {
      // silent — render empty + connect buttons
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
    // Surface OAuth return-trip feedback
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail') === 'connected') {
      const email = params.get('email');
      const mismatch = params.get('mismatch') === '1';
      if (mismatch) {
        toast.warning(`Connected ${email || 'a Gmail account'} — but it wasn't the one you started with. Disconnect and retry if needed.`);
      } else {
        toast.success(`Gmail connected${email ? `: ${email}` : ''}`);
      }
      const url = new URL(window.location);
      ['gmail', 'email', 'mismatch'].forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, '', url);
    } else if (params.get('gmail') === 'error') {
      toast.error('Could not connect Gmail. Please try again.');
      const url = new URL(window.location);
      ['gmail', 'reason'].forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, '', url);
    }
  }, []);

  const handleConnect = async (senderEmail) => {
    setConnectingEmail(senderEmail);
    try {
      const res = await adminApi.get('/api/admin/email/gmail/connect', {
        params: { sender: senderEmail },
      });
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start Gmail connection');
      setConnectingEmail(null);
    }
  };

  const handleDisconnect = async (senderEmail) => {
    if (!window.confirm(`Disconnect ${senderEmail}? Emails routed through this account will stop sending until you reconnect.`)) return;
    try {
      await adminApi.post('/api/admin/email/gmail/disconnect', null, {
        params: { email: senderEmail },
      });
      toast.success(`Disconnected ${senderEmail}`);
      fetchAccounts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const handleTest = async (senderEmail) => {
    setTestingEmail(senderEmail);
    try {
      const res = await adminApi.post('/api/admin/email/gmail/test', {
        sender: senderEmail,
      });
      toast.success(res.data?.message || `Test email sent from ${senderEmail}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test send failed');
    } finally {
      setTestingEmail(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-neutral-500" data-testid="gmail-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading email accounts…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="gmail-accounts-panel">
      <div className="flex items-center gap-2 text-xs text-neutral-500 font-medium uppercase tracking-wide">
        <Mail className="w-3.5 h-3.5" /> Connected Gmail accounts
      </div>
      <div className="flex flex-col gap-1.5">
        {accounts.map((acc) => (
          <AccountRow
            key={acc.email}
            account={acc}
            onConnect={() => handleConnect(acc.email)}
            onDisconnect={() => handleDisconnect(acc.email)}
            onTest={() => handleTest(acc.email)}
            connecting={connectingEmail === acc.email}
            testing={testingEmail === acc.email}
          />
        ))}
      </div>
    </div>
  );
}

function AccountRow({ account, onConnect, onDisconnect, onTest, connecting, testing }) {
  const { email, label, connected } = account;
  return (
    <div
      className="flex items-center justify-between gap-3 px-3 py-2 border border-white/40 rounded-lg bg-white/40"
      data-testid={`gmail-row-${email}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {connected ? (
          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
        ) : (
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium text-[#1a1a2e] truncate">{email}</div>
          <div className="text-[11px] text-neutral-500">{label}</div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {connected ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={onTest}
              disabled={testing}
              className="h-7 px-2 text-xs text-indigo-700 hover:bg-indigo-50"
              data-testid={`gmail-test-btn-${email}`}
              title={`Send a test email from ${email}`}
            >
              {testing ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Send className="w-3 h-3 mr-1" />
              )}
              Send test
            </Button>
            <button
              onClick={onDisconnect}
              className="p-1 hover:bg-red-50 rounded text-neutral-400 hover:text-red-500 transition-colors"
              title={`Disconnect ${email}`}
              data-testid={`gmail-disconnect-btn-${email}`}
            >
              <Unplug className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={onConnect}
            disabled={connecting}
            className="h-7 px-2 text-xs"
            data-testid={`gmail-connect-btn-${email}`}
          >
            {connecting ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Plus className="w-3 h-3 mr-1" />
            )}
            Connect
          </Button>
        )}
      </div>
    </div>
  );
}
